if (!customElements.get('jiec-product-discovery')) {
  const discoveryCore = window.JiecProductDiscoveryCore;

  customElements.define(
    'jiec-product-discovery',
    class JiecProductDiscovery extends HTMLElement {
      connectedCallback() {
        this.activeNeed = this.readNeedFromUrl();
        this.searchQuery = this.readSearchQueryFromUrl();
        window.jiecProductDiscoveryNeed = this.activeNeed;
        this.refresh();

        this.sectionObserver = new MutationObserver(() => this.refresh());
        this.sectionObserver.observe(this.parentElement, { childList: true, subtree: true });
      }

      disconnectedCallback() {
        this.sectionObserver?.disconnect();
      }

      refresh() {
        this.syncControls();
        this.applyFilter();
      }

      onFilterInput(event) {
        const control = event.target.closest('[data-jiec-discovery-filter]');
        if (!control?.checked) return;
        event.preventDefault();
        event.stopPropagation();
        this.setActiveNeed(control.dataset.jiecDiscoveryFilter);
        if (window.matchMedia('(min-width: 750px)').matches) {
          control.closest('details')?.removeAttribute('open');
        }
      }

      onSearchInput(event) {
        const control = event.target.closest('[data-jiec-product-search]');
        if (!control) return;
        this.setSearchQuery(control.value);
      }

      onSearchKeydown(event) {
        const control = event.target.closest('[data-jiec-product-search]');
        if (event.key !== 'Enter' || !control) return;
        event.preventDefault();
        event.stopPropagation();

        if (window.matchMedia('(max-width: 749px)').matches) {
          control
            .closest('.mobile-facets__disclosure')
            ?.querySelector(':scope > summary')
            ?.click();
        }
      }

      onResetClick(event) {
        if (event.target.closest('[data-jiec-discovery-clear-all]')) {
          this.setActiveNeed('all', { updateUrl: false });
          return;
        }

        const control = event.target.closest('[data-jiec-discovery-reset]');
        if (!control) return;
        event.preventDefault();
        event.stopPropagation();
        this.setActiveNeed('all');
      }

      readNeedFromUrl() {
        return discoveryCore.readNeedFromSearch(window.location.search);
      }

      readSearchQueryFromUrl() {
        return discoveryCore.readProductSearchFromSearch(window.location.search);
      }

      getActiveNeedSlug() {
        return discoveryCore.slugForNeed(this.activeNeed);
      }

      updateUrl({ replace = false } = {}) {
        const url = new URL(window.location.href);
        const activeNeedSlug = this.getActiveNeedSlug();
        url.search = discoveryCore.updateSearch(url.search, this.activeNeed);
        url.search = discoveryCore.updateProductSearch(url.search, this.searchQuery);

        const nextUrl = `${url.pathname}${url.search}${url.hash}`;
        const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (nextUrl === currentUrl) return;

        history[replace ? 'replaceState' : 'pushState'](
          {
            ...(history.state || {}),
            searchParams: url.searchParams.toString(),
            jiecBestFor: activeNeedSlug,
            jiecProductSearch: this.searchQuery,
          },
          '',
          nextUrl
        );
      }

      setActiveNeed(need, { updateUrl = true } = {}) {
        this.activeNeed = discoveryCore.normalizeNeed(need);
        window.jiecProductDiscoveryNeed = this.activeNeed;
        if (updateUrl) this.updateUrl();
        this.syncControls();
        this.applyFilter();
      }

      setSearchQuery(query, { updateUrl = true } = {}) {
        this.searchQuery = String(query || '');
        if (updateUrl) this.updateUrl({ replace: true });
        this.syncControls();
        this.applyFilter();
      }

      syncControls() {
        document.querySelectorAll('[data-jiec-discovery-filter]').forEach((control) => {
          control.checked = control.dataset.jiecDiscoveryFilter === this.activeNeed;
        });

        document.querySelectorAll('[data-jiec-product-search]').forEach((control) => {
          if (control.value !== this.searchQuery) control.value = this.searchQuery;
        });

        document.querySelectorAll('[data-jiec-discovery-selected]').forEach((target) => {
          const label = this.activeNeed === 'all' ? '0 selected' : '1 selected';
          if (target.textContent !== label) target.textContent = label;
        });

        const activeNeedSlug = this.getActiveNeedSlug();
        document.querySelectorAll('[data-jiec-discovery-param]').forEach((input) => {
          input.value = activeNeedSlug;
          input.disabled = !activeNeedSlug;
        });

        document.querySelectorAll('[data-jiec-discovery-active]').forEach((activeFilter) => {
          const isActive = this.activeNeed !== 'all';
          activeFilter.hidden = !isActive;
          activeFilter.querySelectorAll('[data-jiec-discovery-active-label]').forEach((label) => {
            if (label.textContent !== this.activeNeed) label.textContent = this.activeNeed;
          });
        });

        document.querySelectorAll('[data-jiec-discovery-clear-all]').forEach((clearAll) => {
          const activeFacets = clearAll.closest('.active-facets');
          const hasShopifyFilter = [...activeFacets.querySelectorAll('facet-remove')].some(
            (facet) =>
              facet !== clearAll &&
              !facet.hasAttribute('data-jiec-discovery-active') &&
              !facet.hidden
          );
          clearAll.hidden = this.activeNeed === 'all' && !this.searchQuery && !hasShopifyFilter;
        });
      }

      applyFilter() {
        const productGrid = document.getElementById('product-grid');
        if (!productGrid) return;

        const products = [...productGrid.querySelectorAll('[data-jiec-needs]')];
        let visibleCount = 0;

        products.forEach((product) => {
          const assessmentTerms =
            window.JiecAssessmentData?.searchTermsByHandle?.[product.dataset.jiecProductHandle] || [];
          const searchText = [product.dataset.jiecSearchText, ...assessmentTerms].join(' ');
          const isVisible =
            discoveryCore.matchesNeed(product.dataset.jiecNeeds, this.activeNeed) &&
            discoveryCore.matchesSearch(searchText, this.searchQuery);
          product.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        const productCount = `${visibleCount} ${visibleCount === 1 ? 'product' : 'products'}`;
        document
          .querySelectorAll('#ProductCountDesktop, #ProductCount, .mobile-facets__count')
          .forEach((target) => {
            if (target.textContent.trim() !== productCount) target.textContent = productCount;
          });

        document.querySelectorAll('[data-jiec-discovery-empty]').forEach((emptyState) => {
          const hasActiveDiscovery = this.activeNeed !== 'all' || Boolean(this.searchQuery.trim());
          const customFilterRemovedEveryServerResult = products.length > 0 && visibleCount === 0;
          emptyState.hidden = !hasActiveDiscovery || !customFilterRemovedEveryServerResult;
        });
      }
    }
  );
}

if (!window.jiecProductDiscoveryEventsBound) {
  window.jiecProductDiscoveryEventsBound = true;

  document.addEventListener(
    'input',
    (event) => {
      const controller = document.querySelector('jiec-product-discovery');
      if (event.target.closest('[data-jiec-product-search]')) {
        event.stopPropagation();
        controller?.onSearchInput(event);
        return;
      }
      controller?.onFilterInput(event);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      document.querySelector('jiec-product-discovery')?.onResetClick(event);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      document.querySelector('jiec-product-discovery')?.onSearchKeydown(event);
    },
    true
  );

  window.addEventListener('popstate', () => {
    const controller = document.querySelector('jiec-product-discovery');
    if (!controller) return;
    controller.setActiveNeed(controller.readNeedFromUrl(), { updateUrl: false });
    controller.setSearchQuery(controller.readSearchQueryFromUrl(), { updateUrl: false });
  });
}

window.jiecProductDiscoveryReset = () => {
  document.querySelector('jiec-product-discovery')?.setActiveNeed('all');
};

window.jiecProductDiscoveryClearAll = (event) => {
  event.preventDefault();
  event.stopImmediatePropagation();
  const controller = document.querySelector('jiec-product-discovery');
  controller?.setActiveNeed('all', { updateUrl: false });
  controller?.setSearchQuery('', { updateUrl: false });
  window.location.assign(event.currentTarget.href);
  return false;
};
